import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp } from "./Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "The dark is honest",
    body: "We chase window light, candlelight, last light. Shadow isn't the absence of a photograph — it's where the emotion hides, waiting to be found.",
  },
  {
    n: "02",
    title: "Unposed, unrepeatable",
    body: "Your grandmother's hands. A laugh mid-vow. The moment before the moment. We don't manufacture memories — we wait for them, quietly.",
  },
  {
    n: "03",
    title: "Grain over perfection",
    body: "We shoot like it's film because feeling beats flawless. Slightly imperfect, deeply human frames that will still move you in forty years.",
  },
];

const ABOUT_IMG = "/photos/bride-bridge.webp";

export const Manifesto = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="manifesto" className="relative px-6 md:px-12 py-28 md:py-44 bg-[#0A0A0A]" data-testid="manifesto-section">
      <FadeUp className="mb-20 md:mb-32">
        <p className="text-xs tracking-[0.4em] uppercase text-[#C9A26B] mb-4">The Manifesto</p>
        <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#F5F4F0] max-w-3xl leading-[1.05]">
          We don't take pictures. <span className="font-accent italic text-[#C9A26B]">We keep them.</span>
        </h2>
      </FadeUp>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-8">
        <div ref={imgRef} className="relative md:col-span-5 overflow-hidden aspect-[3/4] rounded-sm">
          <motion.img
            style={{ y: imgY }}
            src={ABOUT_IMG}
            alt="Bride in embroidered red and blush attire"
            className="photo-tone w-full h-[116%] object-cover scale-110"
            data-testid="manifesto-image"
          />
          <div className="absolute inset-0 bg-[#0A0A0A]/30" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0A0A0A]/80 to-transparent" />
        </div>
        <div className="md:col-span-6 md:col-start-7 flex flex-col gap-20 md:gap-28 md:pt-10">
          {CHAPTERS.map((c, i) => (
            <FadeUp key={c.n} delay={i * 0.08} className="grid grid-cols-12 gap-4 items-start" >
              <span className="col-span-4 font-serif font-light text-7xl md:text-8xl leading-none text-stroke select-none" data-testid={`chapter-number-${c.n}`}>
                {c.n}
              </span>
              <div className="col-span-8">
                <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-[#F5F4F0] mb-4">{c.title}</h3>
                <p className="text-sm md:text-base text-[#A3A3A3] leading-relaxed">{c.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};
